import { useCallback, useEffect, useState } from 'react'
import { DialogContent } from '@/components/ui/dialog'
import { GridColumnProps } from '@progress/kendo-react-grid'
import { UserResponse } from 'api/Identity/Types/UserResponse'
import {
  Item,
  SelectDataFetchOverviewTable,
  SelectOverviewTableProps,
} from 'components/SelectOverviewTable'
import { createGridState } from 'features/DataGrid'
import { normalizeUsersSearchQuery } from 'routes/Administration/Users/normalizer'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, BuildQueryProps, FilterLogic } from 'utils/QueryHelpers'

import { inputFilterCell } from '../Filter/FilterCell'

export interface GridColumn extends GridColumnProps {
  width?: number
}

export type SelectItemsEvent = { filterId: string; value: Item<UserResponse>[] }
export type SelectItemEvent = { filterId: string; value: Item<UserResponse> }

export interface SelectAssignTableProps
  extends Omit<
    SelectOverviewTableProps<UserResponse>,
    'items' | 'isLoading' | 'columns' | 'onSingleSelect' | 'onMultipleSelect'
  > {
  onSingleSelect?: ({ filterId, value }: SelectItemEvent) => void
  onMultipleSelect?: ({ filterId, value }: SelectItemsEvent) => void
  filterId?: string
  selectText?: string
  query: BuildQueryProps
}

const InputFilterCell = inputFilterCell({
  options: {
    operator: 'like',
  },
})

export const SelectUsersAssignTable: React.FC<SelectAssignTableProps> = (props) => {
  const identityApiClient = useApiClient(IdentityApiClient)

  const [filteredUsers, setFilteredUsers] = useState<Item<UserResponse>[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    createGridState(getPaginationInitialState(), {})
  )

  const getQuery = useCallback(
    () => {
      return (
        buildQuery(
          normalizeUsersSearchQuery(createSearchQuery(gridFilterState.filter, props.query))
        ) ?? null
      )
    },
    [gridFilterState.filter] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    const searchQuery = getQuery()
    return identityApiClient.getDetailedAdministrationUsers({
      search: searchQuery,
      pageIndex: gridFilterState.pageIndex,
      pageSize: gridFilterState.pageSize,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: users, isLoading } = useFetchOne(callback)

  useEffect(() => {
    if (users && users?.items?.length >= 0) {
      setFilteredUsers(users.items || [])
      setGridFilterState({
        ...gridFilterState,
        total: users.totalResults,
      })
    }
  }, [users]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (users && users?.items?.length >= 0) {
      forceRefresh()
    }
  }, [gridFilterState.pageIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (users && users?.items?.length >= 0) {
      const timeoutId = setTimeout(() => forceRefresh(), 500)
      return () => clearTimeout(timeoutId)
    }
  }, [gridFilterState.filter]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGridPageChange = async (state: GridFilterActionState) => {
    setGridFilterState(state)
  }

  const onFilterChange = (state: GridFilterActionState) => {
    setGridFilterState(state)
  }

  const onSingleSelect = (userSelected: Item<UserResponse>) => {
    if (props.onSingleSelect) {
      props.onSingleSelect({
        filterId: props.filterId!,
        value: userSelected,
      })
    }
  }

  const onMultipleSelect = (usersSelected: Item<UserResponse>[]) => {
    if (props.onMultipleSelect) {
      return props.onMultipleSelect({
        filterId: props.filterId!,
        value: usersSelected.map((x) => ({ ...x, name: `${x.firstName} ${x.lastName}` })),
      })
    }
  }

  return (
    <DialogContent size='3xl'>
      <SelectDataFetchOverviewTable
        {...props}
        items={filteredUsers}
        filterState={gridFilterState}
        columns={[
          {
            title: 'First Name',
            field: 'firstName',
            filterCell: InputFilterCell,
          },
          {
            title: 'Last Name',
            field: 'lastName',
            filterCell: InputFilterCell,
          },
        ]}
        isLoading={isLoading}
        selectText={props.selectText}
        onGridPageChange={handleGridPageChange}
        onFilterChange={onFilterChange}
        onSingleSelect={props.onSingleSelect && onSingleSelect}
        onMultipleSelect={props.onMultipleSelect && onMultipleSelect}
      />
    </DialogContent>
  )
}

const createSearchQuery = (
  filterDescriptor?: CompositeFilterDescriptor,
  defaultFilters?: BuildQueryProps
): BuildQueryProps => {
  return {
    ...filterDescriptor,
    logic: filterDescriptor?.logic || FilterLogic.AND,
    filters: filterDescriptor?.filters || [],
    defaultFilters: defaultFilters?.defaultFilters,
  }
}
